<template>
  <div class="cm-set">
    <div class="cm-header">
      <span
        class="cm-dot"
        :class="value.isactive ? 'cm-dot--on' : 'cm-dot--off'"
        @click="value.isactive = !value.isactive"
      />
      <span
        class="cm-add"
        @click="addMod"
      >
        <feather-icon
          icon="PlusIcon"
          size="10"
        /> модификация
      </span>
    </div>
    <div
      v-for="(mod, mi) in value.list"
      :key="mi"
      class="cm-line"
    >
      <span
        class="cm-dot"
        :class="mod.isactive ? 'cm-dot--on' : 'cm-dot--off'"
        @click="mod.isactive = !mod.isactive"
      />
      <!-- dest -->
      <span
        class="cm-word"
        :class="{ 'cm-word--empty': !mod.destentity }"
        @click="open(mi, 'dest')"
      >{{ destLabel(mod) }}</span>
      <span class="cm-eq">=</span>
      <!-- oper -->
      <span
        class="cm-word cm-word--oper"
        @click="open(mi, 'oper')"
      >{{ operLabel(mod) }}</span>
      <!-- value word -->
      <template v-if="needsFieldPicker(mod)">
        <span
          class="cm-word"
          :class="{ 'cm-word--empty': !hasValEntity(mod) }"
          @click="open(mi, 'val')"
        >{{ valEntityLabel(mod) }}</span>
      </template>
      <template v-else-if="needsRefPicker(mod)">
        <span
          class="cm-word"
          :class="{ 'cm-word--empty': !hasRef(mod) }"
          @click="open(mi, 'ref')"
        >{{ refLabel(mod) }}</span>
      </template>
      <template v-else-if="needsRefValuePicker(mod)">
        <span
          class="cm-word"
          :class="{ 'cm-word--empty': !hasRefValue(mod) }"
          @click="open(mi, 'refval')"
        >{{ refValueLabel(mod) }}</span>
      </template>
      <template v-else-if="mod.oper && mod.oper.sid === 'sql'">
        <b-form-input
          :value="textValue(mod)"
          size="sm"
          class="cm-input"
          placeholder="SQL"
          @input="onTextInput(mod, $event)"
          @click.native.stop
        />
      </template>
      <template v-else>
        <b-form-input
          :value="textValue(mod)"
          size="sm"
          class="cm-input"
          placeholder="значение"
          @input="onTextInput(mod, $event)"
          @click.native.stop
        />
      </template>
      <span
        class="cm-del"
        @click="removeMod(mi)"
      >
        <feather-icon
          icon="XIcon"
          size="10"
        />
      </span>
      <!-- dropdown panel (rendered below the line) -->
      <div
        v-if="activeRow === mi && activeKind"
        class="cm-panel"
      >
        <b-form-input
          :ref="'pi_' + mi + '_' + activeKind"
          v-model="search"
          size="sm"
          placeholder="Поиск..."
          autocomplete="off"
          @input="onSearch"
          @keydown.esc="close"
          @keydown.enter.prevent="pickFirst(mi)"
        />
        <div
          v-if="filtered.length"
          class="cm-list"
        >
          <div
            v-for="item in filtered"
            :key="item.value"
            class="cm-opt"
            @click.stop="pick(mi, item)"
          >{{ item.text }}</div>
        </div>
        <div
          v-else-if="search"
          class="cm-empty"
        >
          Не найдено
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BFormInput } from 'bootstrap-vue'
import useJwt from '@/cubus/jwt/useJwt'
import useCubus from '@/cubus/services/useCubus'

let cachedModoperRef = null
let modoperRefLoading = false
const modoperRefCallbacks = []

let cachedEntityRef = null
let entityRefLoading = false
const entityRefCallbacks = []

let cachedScalarRef = null
let scalarRefLoading = false
const scalarRefCallbacks = []

let cachedFormulaRef = null
let formulaRefLoading = false
const formulaRefCallbacks = []

let cachedFunctionRef = null
let functionRefLoading = false
const functionRefCallbacks = []

function loadRef(sid, cache, setCache, loading, setLoading, cbs, setter) {
  if (cache) { setter(cache); return }
  if (loading) { cbs.push(setter); return }
  setLoading(true)
  useJwt.query({ query: { method: 'entref', param: { entityid: 0, entitysid: sid } } })
    .then(r => { if (r.data.thread) pollRef(r.data.thread, setCache, setLoading, cbs, setter) })
    .catch(() => { setLoading(false) })
}

function pollRef(thread, setCache, setLoading, cbs, setter) {
  setTimeout(() => {
    useJwt.query({ query: { method: 'thread', param: { threadname: thread } } })
      .then(r => {
        if (r.data.thread && r.data.thread.status === 'done') {
          const items = (r.data.thread.result.ref || []).map(x => ({ value: x.value, sid: x.sid, title: x.title || `(${x.value})` }))
          setCache(items); setter(items); setLoading(false)
          cbs.forEach(cb => cb(items)); cbs.length = 0
        } else { pollRef(thread, setCache, setLoading, cbs, setter) }
      })
      .catch(() => { setLoading(false) })
  }, 500)
}

export default {
  name: 'CbsModificationConstructor',
  components: { BFormInput },
  props: {
    value: { type: Object, required: true },
    fields: { type: Array, default: () => [] },
    paramFields: { type: Array, default: () => [] },
  },
  data() {
    return {
      argTypes: cachedModoperRef || [],
      entityRefItems: cachedEntityRef || [],
      scalarRefItems: cachedScalarRef || [],
      formulaRefItems: cachedFormulaRef || [],
      functionRefItems: cachedFunctionRef || [],
      refValueCache: {},
      activeRow: -1,
      activeKind: null,
      search: '',
      source: [],
      filtered: [],
    }
  },
  created() {
    if (!cachedModoperRef) this.loadModoperRef()
    this.ensureRefs()
  },
  mounted() {
    this._outsideClick = e => {
      if (this.activeKind && !e.target.closest('.cm-line')) this.close()
    }
    document.addEventListener('click', this._outsideClick)
  },
  beforeDestroy() {
    document.removeEventListener('click', this._outsideClick)
  },
  methods: {
    // --- labels ---
    destLabel(m) { return (m.destentity && (m.destentity.title || m.destentity.entitysid)) || '(поле)' },
    operLabel(m) { return (m.oper && (m.oper.title || m.oper.name || m.oper.sid)) || '(тип)' },
    valEntityLabel(m) {
      const v = m.value && m.value.value
      return (v && typeof v === 'object' && (v.title || v.entitysid)) || '(поле)'
    },
    refLabel(m) {
      const key = this.refKey(m)
      const r = m[key]
      return (r && (r.title || r.sid)) || '(выбор)'
    },
    needsFieldPicker(m) { return m.oper && (m.oper.sid === 'entity' || m.oper.sid === 'paramentity' || m.oper.sid === 'sourceentity') },
    needsRefPicker(m) { return m.oper && (m.oper.sid === 'function' || m.oper.sid === 'scalar' || m.oper.sid === 'formula') },
    needsRefValuePicker(m) {
      if (!m.destentity || !m.oper) return false
      if (m.oper.sid !== 'value') return false
      // Check explicit datatype first
      const dt = m.destentity.datatype
      if (dt === 'ref' || dt === 'refsid') return true
      // Fallback: check if ref values were loaded for this entity (auto-detected ref type)
      const eid = m.destentity.value
      if (eid && this.refValueCache[eid] && this.refValueCache[eid].length > 0) return true
      return false
    },
    hasRefValue(m) {
      const v = m.value && m.value.value
      return v && typeof v === 'object' && (v.title || v.value)
    },
    refValueLabel(m) {
      const v = m.value && m.value.value
      if (!v) return '(выбор)'
      if (typeof v === 'object') return v.title || `(${v.value})` || '(выбор)'
      return String(v)
    },
    hasValEntity(m) { const v = m.value && m.value.value; return v && typeof v === 'object' && (v.title || v.entitysid) },
    hasRef(m) { const r = m[this.refKey(m)]; return r && (r.title || r.sid || r.id) },
    refKey(m) {
      if (!m.oper) return 'entity'
      const s = m.oper.sid
      if (s === 'scalar') return 'scalar'
      if (s === 'formula') return 'formula'
      if (s === 'function') return 'function'
      if (s === 'value') return 'value'
      return 'entity'
    },
    textValue(m) {
      if (!m.value || !m.value.value) return ''
      return typeof m.value.value === 'object' ? (m.value.value.value || '') : m.value.value
    },
    onTextInput(m, val) {
      if (!m.value) this.$set(m, 'value', { value: { value: null } })
      if (!m.value.value || typeof m.value.value !== 'object') this.$set(m.value, 'value', { value: null })
      m.value.value.value = val
    },

    // --- picker ---
    open(mi, kind) {
      if (this.activeRow === mi && this.activeKind === kind) { this.close(); return }
      this.search = ''
      this.source = this.buildSource(mi, kind)
      this.filtered = this.source
      this.activeRow = mi
      this.activeKind = kind
      const mod = this.value.list[mi]
      if (kind === 'val' && mod.oper && mod.oper.sid === 'sourceentity' && !this.paramFields.length) {
        this.$emit('ensure-source-fields')
      }
      if (kind === 'refval') {
        const entId = mod.destentity && mod.destentity.value
        if (entId && !this.refValueCache[entId]) {
          this.loadRefValue(entId).then(() => {
            if (this.activeRow === mi && this.activeKind === 'refval') {
              this.source = this.buildSource(mi, kind)
              this.filtered = this.search.trim() ? this.filtered : this.source
              if (!this.search.trim()) this.filtered = this.source
            }
          })
        }
      }
      this.$nextTick(() => {
        const ref = this.$refs[`pi_${mi}_${kind}`]
        const el = Array.isArray(ref) ? ref[0] : ref
        if (el) el.focus()
      })
    },
    close() { this.activeRow = -1; this.activeKind = null; this.search = ''; this.source = []; this.filtered = [] },
    onSearch() {
      if (!this.search.trim()) { this.filtered = this.source; return }
      const parts = this.search.trim().toLowerCase().split(/\s+/)
      const re = new RegExp(parts.map(p => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('.*'))
      this.filtered = this.source.filter(i => re.test((i.text || '').toLowerCase()))
    },
    buildSource(mi, kind) {
      const mod = this.value.list[mi]
      if (kind === 'dest') return this.fields.map(f => ({ value: f.value, text: f.title || f.entitysid || String(f.value), _f: f }))
      if (kind === 'oper') return this.argTypes.map(t => ({ value: t.sid, text: t.title, _t: t }))
      if (kind === 'val') {
        const src = mod.oper && (mod.oper.sid === 'paramentity' || mod.oper.sid === 'sourceentity') ? this.paramFields : this.fields
        return src.map(f => ({ value: f.value, text: f.title || f.entitysid || String(f.value), _f: f }))
      }
      if (kind === 'ref') {
        const k = this.refKey(mod)
        let items
        if (k === 'scalar') items = this.scalarRefItems
        else if (k === 'formula') items = this.formulaRefItems
        else if (k === 'function') items = this.functionRefItems
        else items = this.entityRefItems
        return items.map(r => ({ value: r.value, text: r.title, _r: r }))
      }
      if (kind === 'refval') {
        const eid = mod.destentity && mod.destentity.value
        if (eid && this.refValueCache[eid]) {
          return this.refValueCache[eid].map(r => ({ value: r.value, text: r.title, _r: r }))
        }
        return []
      }
      return []
    },
    pickFirst(mi) { if (this.filtered.length) this.pick(mi, this.filtered[0]) },
    pick(modIdx, item) {
      const mod = this.value.list[modIdx]
      const kind = this.activeKind
      if (kind === 'dest') {
        this.$set(mod, 'destentity', item._f || null)
        // If oper is already 'value', proactively load ref values for the new dest entity
        if (mod.oper && mod.oper.sid === 'value' && item._f && item._f.value) {
          this.ensureRefForEntity(mod, item._f.value)
        }
      } else if (kind === 'oper') {
        const t = item._t
        this.$set(mod, 'oper', t ? { sid: t.sid, title: t.title, value: t.value } : { sid: item.value, title: item.text })
        this.$set(mod, 'value', { value: { value: null } })
        this.$set(mod, 'entity', {
          value: null, id: null, sid: null, title: null,
        })
        this.$set(mod, 'scalar', {
          value: null, id: null, sid: null, title: null,
        })
        this.$set(mod, 'formula', {
          value: null, id: null, sid: null, title: null,
        })
        this.$set(mod, 'function', {
          value: null, id: null, sid: null, title: null,
        })
        const operSid = t ? t.sid : item.value
        if (operSid === 'formula') this.loadFormulaRef()
        else if (operSid === 'scalar') this.loadScalarRef()
        else if (operSid === 'function') this.loadFunctionRef()
        // If switching to 'value' and dest entity exists, proactively load ref values
        if (operSid === 'value' && mod.destentity && mod.destentity.value) {
          this.ensureRefForEntity(mod, mod.destentity.value)
        }
      } else if (kind === 'val') {
        if (!mod.value) this.$set(mod, 'value', { value: null })
        this.$set(mod.value, 'value', item._f || null)
        if (mod.oper && mod.oper.sid === 'sourceentity') {
          const selectedItem = item._f
          this.$set(mod, 'entity', {
            value: selectedItem.value, id: selectedItem.value, sid: selectedItem.entitysid, title: selectedItem.title,
          } || null)
        }
      } else if (kind === 'ref') {
        const k = this.refKey(mod)
        const r = item._r
        this.$set(mod, k, r ? {
          value: r.value, id: r.value, sid: r.sid, title: r.title,
        } : {
          value: null, id: null, sid: null, title: null,
        })
      } else if (kind === 'refval') {
        if (!mod.value) this.$set(mod, 'value', { value: null })
        const r = item._r
        this.$set(mod.value, 'value', r ? { value: r.value, title: r.title } : null)
      }
      this.close()
    },

    // --- add/remove ---
    addMod() {
      if (!this.value.list) this.$set(this.value, 'list', [])
      this.value.list.push({
        isactive: true,
        name: `Modification ${this.value.list.length + 1}`,
        descr: '',
        destentity: null,
        oper: { sid: 'value', title: 'Value', value: 1 },
        value: { value: { value: null } },
        entity: { id: null, sid: null, title: null },
        scalar: { id: null, sid: null, title: null },
        formula: { id: null, sid: null, title: null },
        function: { id: null, sid: null, title: null },
      })
      this.value.isactive = true
    },
    removeMod(i) { this.value.list.splice(i, 1); if (this.activeRow === i) this.close() },

    // --- refs ---
    ensureRefs() {
      if (!this.value.list) return
      if (this.value.list.some(m => m.oper && m.oper.sid === 'function')) this.loadFunctionRef()
      if (this.value.list.some(m => m.oper && m.oper.sid === 'scalar')) this.loadScalarRef()
      if (this.value.list.some(m => m.oper && m.oper.sid === 'formula')) this.loadFormulaRef()
      // Proactively load ref values for 'value' oper — auto-detect ref-type entities
      this.value.list.forEach(m => {
        if (m.oper && m.oper.sid === 'value' && m.destentity && m.destentity.value) {
          this.ensureRefForEntity(m, m.destentity.value)
        }
      })
    },
    // Load ref values for an entity and auto-patch destentity.datatype
    ensureRefForEntity(mod, eid) {
      if (eid in this.refValueCache) {
        if (this.refValueCache[eid].length > 0 && mod.destentity && !mod.destentity.datatype) {
          this.$set(mod.destentity, 'datatype', 'ref')
        }
        return
      }
      this.loadRefValue(eid).then(() => {
        if (this.refValueCache[eid] && this.refValueCache[eid].length > 0 && mod.destentity && !mod.destentity.datatype) {
          this.$set(mod.destentity, 'datatype', 'ref')
        }
      })
    },
    loadModoperRef() { loadRef('modoper', cachedModoperRef, v => { cachedModoperRef = v }, modoperRefLoading, v => { modoperRefLoading = v }, modoperRefCallbacks, items => { this.argTypes = items }) },
    async loadRefValue(entityid) {
      try {
        const ref = await useCubus.loadRef(this, { entityid })
        if (ref && ref.length > 0) {
          const items = ref.map(r => ({ value: r.value, sid: r.sid, title: r.title && r.title !== '' ? r.title : `(${r.value})` }))
          this.$set(this.refValueCache, entityid, items)
        } else {
          // No ref values — not a ref-type entity; cache empty to avoid re-loading
          this.$set(this.refValueCache, entityid, [])
        }
      } catch {
        this.$set(this.refValueCache, entityid, [])
      }
    },
    loadEntityRef() { loadRef('entity', cachedEntityRef, v => { cachedEntityRef = v }, entityRefLoading, v => { entityRefLoading = v }, entityRefCallbacks, items => { this.entityRefItems = items }) },
    loadScalarRef() { loadRef('scalar', cachedScalarRef, v => { cachedScalarRef = v }, scalarRefLoading, v => { scalarRefLoading = v }, scalarRefCallbacks, items => { this.scalarRefItems = items }) },
    loadFunctionRef() { loadRef('function', cachedFunctionRef, v => { cachedFunctionRef = v }, functionRefLoading, v => { functionRefLoading = v }, functionRefCallbacks, items => { this.functionRefItems = items }) },
    loadFormulaRef() { loadRef('formula', cachedFormulaRef, v => { cachedFormulaRef = v }, formulaRefLoading, v => { formulaRefLoading = v }, formulaRefCallbacks, items => { this.formulaRefItems = items }) },
  },
}
</script>

<style lang="scss" scoped>
.cm-set {
  font-size: 12px;
}

.cm-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 0;
  user-select: none;
}

.cm-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  cursor: pointer;
}
.cm-dot--on { background: #28c76f; }
.cm-dot--off { background: #ea5455; }

.cm-add {
  font-size: 11px;
  color: #b9b9c3;
  cursor: pointer;
  padding: 1px 4px;
  border-radius: 3px;
  white-space: nowrap;
  &:hover { color: #28c76f; background: rgba(40, 199, 111, 0.1); }
}

.cm-line {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 0 3px 12px;
  line-height: 1.4;
  flex-wrap: wrap;
}

.cm-word {
  color: #7367f0;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  border-bottom: 1px dashed rgba(115, 103, 240, 0.4);

  &:hover {
    border-bottom-color: #7367f0;
  }
}

.cm-word--empty {
  color: #b9b9c3;
  border-bottom-color: rgba(185, 185, 195, 0.4);
  font-style: italic;

  &:hover {
    color: #7367f0;
    border-bottom-color: #7367f0;
  }
}

.cm-eq {
  color: #b9b9c3;
  user-select: none;
}

.cm-word--oper {
  font-weight: 600;
  color: #ff9f43;
  border-bottom-color: rgba(255, 159, 67, 0.4);

  &:hover {
    border-bottom-color: #ff9f43;
  }
}

.cm-input {
  font-size: 12px;
  height: 22px;
  padding: 0 4px;
  max-width: 140px;
  flex-shrink: 1;
}

.cm-del {
  color: #b9b9c3;
  cursor: pointer;
  padding: 1px 3px;
  border-radius: 3px;
  line-height: 1;
  flex-shrink: 0;
  &:hover { color: #ea5455; background: rgba(234, 84, 85, 0.1); }
}

.cm-panel {
  position: absolute;
  top: 100%;
  left: 12px;
  z-index: 10;
  width: 220px;
  background: #fff;
  border: 1px solid #ebe9f1;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 4px;
}

.cm-list {
  max-height: 200px;
  overflow-y: auto;
  margin-top: 4px;
}

.cm-opt {
  padding: 3px 8px;
  cursor: pointer;
  border-radius: 3px;
  color: #5e5873;
  &:hover { background: #f8f8f8; }
}

.cm-empty {
  padding: 6px 8px;
  color: #b9b9c3;
}

// Dark mode
.dark-layout {
  .cm-add { color: #4b4b5a; }
  .cm-del { color: #4b4b5a; }
  .cm-word { color: #7367f0; }
  .cm-word--empty { color: #4b4b5a; }
  .cm-word--oper { color: #ff9f43; }
  .cm-panel {
    background: #283046;
    border-color: #3b4253;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
  .cm-opt {
    color: #d0d2d6;
    &:hover { background: #3b4253; }
  }
}
</style>
